import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogTitle } from "@/components/ui/alert-dialog"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// Segna il modulo come modificato quando si scrive o si sceglie qualcosa
const DIRTY_TARGET = "[role=combobox],[role=checkbox],[role=switch],[role=radio],button:not([role=tab]):not([data-dialog-close])"

const LeavePrompt = ({ open, onStay, onLeave, onSave, saving }) => (
  <AlertDialog open={open} onOpenChange={(o) => !o && onStay()}>
    <AlertDialogContent className="max-w-sm" data-testid="dialog-leave-prompt">
      <AlertDialogTitle>Hai modifiche non salvate</AlertDialogTitle>
      <AlertDialogDescription>Vuoi salvarle prima di uscire?</AlertDialogDescription>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={onStay} className="h-9 rounded-md px-3 text-sm transition-colors hover:bg-secondary" data-testid="dialog-leave-stay">Continua a modificare</button>
        <button type="button" onClick={onLeave} className="h-9 rounded-md border border-border px-3 text-sm transition-colors hover:bg-secondary" data-testid="dialog-leave-discard">Esci senza salvare</button>
        {onSave && <button type="button" disabled={saving} onClick={onSave} className="h-9 rounded-md bg-primary px-3 text-sm text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50" data-testid="dialog-leave-save">{saving ? "Salvo..." : "Salva ed esci"}</button>}
      </div>
    </AlertDialogContent>
  </AlertDialog>
)

const DialogContent = React.forwardRef(({ className, children, onSave, dirty: dirtyProp, onPointerDownOutside, onEscapeKeyDown, ...props }, ref) => {
  const touched = React.useRef(false)
  const closeRef = React.useRef(null)
  const [ask, setAsk] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  // Se il modulo dice se è modificato usa quello, altrimenti basta aver scritto o scelto qualcosa
  const isDirty = () => (dirtyProp === undefined ? touched.current : dirtyProp)
  const guard = (e) => { if (isDirty()) { e.preventDefault(); setAsk(true) } }
  const leave = () => { touched.current = false; setAsk(false); setTimeout(() => closeRef.current?.click(), 0) }
  const save = async () => {
    setSaving(true)
    try { await onSave(); leave() } catch { setAsk(false) } finally { setSaving(false) }
  }
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        onInputCapture={() => { touched.current = true }}
        onPointerDownCapture={(e) => { if (e.target.closest?.(DIRTY_TARGET) && !e.target.closest?.("[data-testid*=save]")) touched.current = true }}
        onPointerDownOutside={(e) => { guard(e); onPointerDownOutside?.(e) }}
        onEscapeKeyDown={(e) => { guard(e); onEscapeKeyDown?.(e) }}
        {...props}>
        {children}
        <button type="button" data-dialog-close onClick={(e) => { if (isDirty()) setAsk(true); else closeRef.current?.click(); e.stopPropagation() }}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2" data-testid="dialog-close-x">
          <X className="h-4 w-4" />
          <span className="sr-only">Chiudi</span>
        </button>
        <DialogPrimitive.Close ref={closeRef} className="hidden" tabIndex={-1} aria-hidden />
        <LeavePrompt open={ask} onStay={() => setAsk(false)} onLeave={leave} onSave={onSave && save} saving={saving} />
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
